// src/components/ChatInterfaceElevenLabsIntake.jsx
// Conversational Discovery using ElevenLabs Agent with the official widget hidden via DOM tricks.
// - Injects/initializes the ElevenLabs convai web component invisibly (via elevenLabsBridge)
// - Subscribes to widget events and mirrors user/agent turns into our chat feed (ChatBubble)
// - Uses shared Footer composer (no bespoke UI); typed sends go through the hidden widget
// - Mic button in Footer starts/stops the widget mic (bridge click-through into shadow DOM)
// - When a conversation_completed/ended event is observed, we call handleRefreshResults with the chat history

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChatBubble from './ChatBubble';
import Footer from './Footer';
import { ELEVENLABS_CONFIG } from '../config/intakeConfig';
import { createElevenLabsBridge } from '../services/elevenLabsBridge';

const ChatInterfaceElevenLabsIntake = ({
  messages,
  setMessages,
  handleRefreshResults,
  convoStarted = true,
  // We override mic control to drive the hidden widget directly
  isWaitingForResponse // used to disable Footer send if needed
}) => {
  const [status, setStatus] = useState('Widget bridge mode: ElevenLabs Agent');
  const [isRecording, setIsRecording] = useState(false);
  const bridgeRef = useRef(null);
  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Filter out placeholder artifacts like "Conversation captured."
  const filteredMessages = useMemo(() => {
    const isPlaceholder = (s) => {
      const t = (s || '').toString().trim().toLowerCase();
      return (
        !t ||
        t === 'conversation captured.' ||
        t === 'conversation captured' ||
        t === 'user: conversation captured.' ||
        t === 'user: conversation captured' ||
        t === 'agent: conversation captured.' ||
        t === 'agent: conversation captured'
      );
    };
    return (messages || []).filter(
      (m) => (m?.role === 'AI' || m?.role === 'User') && !isPlaceholder(m?.content)
    );
  }, [messages]);

  // Prevent duplicate user bubbles: ignore widget user_message if it matches
  // the last message we already appended optimistically.
  const isDuplicateLastUser = useCallback((txt) => {
    try {
      const hist = messagesRef.current || [];
      if (!hist.length) return false;
      const last = hist[hist.length - 1];
      return last?.role === 'User' && (last?.content || '').trim() === (txt || '').trim();
    } catch {
      return false;
    }
  }, []);

  // Also avoid duplicate AI replies (e.g., widget + fallback arriving later)
  const isDuplicateLastAI = useCallback((txt) => {
    try {
      const hist = messagesRef.current || [];
      if (!hist.length) return false;
      const last = hist[hist.length - 1];
      return last?.role === 'AI' && (last?.content || '').trim() === (txt || '').trim();
    } catch {
      return false;
    }
  }, []);

  // Wait for a single agent_message from the widget bridge up to timeoutMs.
  const waitForAgentReply = useCallback((timeoutMs = 4000) => {
    return new Promise((resolve) => {
      let settled = false;
      let off = null;

      const done = (val) => {
        if (settled) return;
        settled = true;
        try { off && off(); } catch {}
        resolve(val);
      };

      try {
        off = bridgeRef.current?.on?.('agent_message', (payload) => {
          const txt = payload?.text || '';
          if (txt) done(txt);
        });
      } catch {
        // ignore and just rely on timeout
      }

      setTimeout(() => done(null), Math.max(500, timeoutMs));
    });
  }, []);

  const agentId = useMemo(
    () => ELEVENLABS_CONFIG?.AGENT_ID || (typeof window !== 'undefined' && window.ELEVENLABS_AGENT_ID) || 'MxvnsnriCpWRZSm1Pnou',
    []
  );

  // Mount hidden widget and wire events
  useEffect(() => {
    let disposed = false;
    let cleanups = [];

    (async () => {
      try {
        setStatus('Widget loading...');
        const bridge = await createElevenLabsBridge(agentId);
        if (disposed) {
          bridge.destroy?.();
          return;
        }
        bridgeRef.current = bridge;
        setStatus('Widget ready (hidden)');

        // Conversation lifecycle
        cleanups.push(
          bridge.on('conversation_started', () => setStatus('Conversation started')),
        );
        cleanups.push(
          bridge.on('conversation_ended', () => {
            setStatus('Conversation ended');
            setIsRecording(false);
          }),
        );

        // User message events from widget:
        // - For typed sends, we optimistically add the user bubble; bridge emits user_message with {synthetic:true}
        //   which we ignore here.
        // - For voice, bridge will finalize partial transcripts and emit user_message without synthetic => add bubble.
        cleanups.push(
          bridge.on('user_message', (payload) => {
            const text = payload?.text || '';
            const synthetic = payload?.synthetic === true;
            if (!text || synthetic) return; // ignore synthetic; we've already added the bubble optimistically
            if (isDuplicateLastUser(text)) return; // avoid echoing the same user text
            setMessages((prev) => [
              ...prev,
              { role: 'User', content: text, id: `user_${Date.now()}` },
            ]);
          }),
        );

        // Agent message -> append to chat feed
        cleanups.push(
          bridge.on('agent_message', (payload) => {
            const text = payload?.text || '';
            if (!text) return;
            setMessages((prev) => [
              ...prev,
              { role: 'AI', content: text, id: `ai_${Date.now()}` },
            ]);
          }),
        );

        // Completion -> run results
        cleanups.push(
          bridge.on('conversation_completed', async ({ transcript } = {}) => {
            try {
              setStatus('Discovery complete. Generating results...');
              // Use our full visible chat history to drive downstream like existing flow
              // (Transcript is available via transcript or bridgeRef.current.getTranscript())
              const history = messagesRef.current;
              await handleRefreshResults(history);
              setStatus('Results generated');
            } catch (e) {
              setStatus(`Failed to generate results: ${e?.message || e}`);
            }
          }),
        );
      } catch (e) {
        setStatus(`Widget failed to initialize: ${e?.message || e}`);
      }
    })();

    return () => {
      disposed = true;
      try {
        cleanups.forEach((off) => off && off());
      } catch {}
      try {
        bridgeRef.current?.destroy?.();
      } catch {}
      bridgeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, setMessages, handleRefreshResults]);

  // Footer send -> forward to hidden widget and optimistically add user bubble
  const handleSendText = useCallback(
    async (text) => {
      const msg = (text || '').trim();
      if (!msg) return;

      // Optimistic user bubble
      setMessages((prev) => [
        ...prev,
        { role: 'User', content: msg, id: `user_${Date.now()}` },
      ]);

      // API-first: call our backend route to get text reply
      try {
        setStatus('Sending to agent (API)...');
        const resp = await fetch('/api/elevenlabs/agent-text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: msg, agentId: agentId, timeoutMs: 60000 })
        });
        const ok = resp.ok;
        const data = await resp.json().catch(() => ({}));
        if (ok) {
          const reply = (data?.replyText && data.replyText.trim()) || '';
          if (reply && !isDuplicateLastAI(reply)) {
            setMessages((prev) => [
              ...prev,
              { role: 'AI', content: reply, id: `ai_${Date.now()}` },
            ]);
            setStatus('Agent replied (API)');
            return;
          }
          setStatus('API returned no text; trying widget...');
        } else {
          const reason = data?.reason || data?.message || `${resp.status} ${resp.statusText}`;
          setStatus(`API error: ${reason}; trying widget...`);
        }
      } catch (e) {
        setStatus(`API call failed: ${e?.message || e}; trying widget...`);
      }

      // As a fallback, try sending through the hidden widget
      try {
        const injected = await bridgeRef.current?.sendText?.(msg);
        setStatus(injected ? 'Sent to widget' : 'Widget send (fallback events)');
      } catch (e) {
        setStatus(`Widget send failed: ${e?.message || e}`);
      }

      // Wait for an agent reply from the widget for a bit longer
      try {
        const reply = await waitForAgentReply(8000);
        if (reply && !isDuplicateLastAI(reply)) {
          setMessages((prev) => [
            ...prev,
            { role: 'AI', content: reply, id: `ai_${Date.now()}` },
          ]);
          setStatus('Agent replied (widget)');
          return;
        }
        setStatus('No text from agent (API+widget)');
      } catch (e) {
        setStatus(`Fallback receive failed: ${e?.message || e}`);
      }
    },
    [setMessages, agentId, waitForAgentReply, isDuplicateLastAI]
  );

  // Mic controls -> drive the hidden widget
  const onSendVoice = useCallback(async () => {
    try {
      await bridgeRef.current?.startVoice?.();
      setIsRecording(true);
      setStatus('Listening (widget mic)');
    } catch (e) {
      setStatus(`Mic start failed: ${e?.message || e}`);
    }
  }, []);

  const onStopVoice = useCallback(async () => {
    try {
      await bridgeRef.current?.stopVoice?.();
      setIsRecording(false);
      setStatus('Mic stopped');
    } catch (e) {
      setStatus(`Mic stop failed: ${e?.message || e}`);
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        flex: 1,
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        borderRadius: '0px',
        padding: '0px',
        position: 'relative'
      }}
    >
      {/* Scrollable chat feed */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: '#FFFFFF',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: convoStarted ? '120px' : '20px',
          paddingTop: '20px',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#F9FAFB' },
          '&::-webkit-scrollbar-thumb': { background: '#E5E7EB', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#D1D5DB' }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '800px', padding: '0 16px' }}>
          {/* Header / status */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Conversational Discovery (Widget bridge).
            </Typography>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Agent: {agentId}
            </Typography>
            <Typography variant="caption" sx={{ color: '#0F766E' }}>
              {status}
            </Typography>
          </Box>

          {/* Messages feed */}
          {filteredMessages.map((m, idx) => {
            if (!m?.content) return null;
            const isFirstInSequence =
              idx === 0 || (filteredMessages[idx - 1] && filteredMessages[idx - 1].role !== m.role);
            return (
              <Box
                key={m.id || idx}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: m.role === 'User' ? 'flex-end' : 'flex-start',
                  mb: 2,
                  width: '100%',
                  maxWidth: '100%',
                  paddingRight: m.role === 'User' ? 8 : 0
                }}
              >
                <ChatBubble message={m.content} isAI={m.role === 'AI'} isFirstInSequence={isFirstInSequence} />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Shared Footer composer (no bespoke buttons). Only show when convo started */}
      {convoStarted && (
        <Footer
          onSendText={handleSendText}
          onSendVoice={onSendVoice}
          onStopVoice={onStopVoice}
          isRecording={isRecording}
          disabled={!!isWaitingForResponse}
          leftSidebarOpen={false}
          drawerWidth={0}
          collapsedWidth={0}
          rightSidebarOpen={false}
          rightSidebarWidth={0}
          placeholder="Type your requirements..."
        />
      )}
    </Box>
  );
};

export default ChatInterfaceElevenLabsIntake;
